import { srcSet } from "@ui/utils/image";

interface ImageSet {
  src: string;
  src2x: string;
  src3x: string;
}

type Breakpoint = "sm" | "md" | "lg" | "xl" | "2xl";

const BREAKPOINT_VALUES: Record<Breakpoint, string> = {
  sm: "640px",
  md: "768px",
  lg: "1024px",
  xl: "1280px",
  "2xl": "1536px",
};

interface ResponsiveImageProps {
  /** Image sources for viewports below the breakpoint */
  mobile: ImageSet;
  /** Image sources for viewports at or above the breakpoint */
  desktop: ImageSet;
  /** Alt text for the image. Use empty string for decorative images. */
  alt: string;
  /** Intrinsic width of the mobile image in pixels */
  mobileWidth: number;
  /** Intrinsic height of the mobile image in pixels */
  mobileHeight: number;
  /** Intrinsic width of the desktop image in pixels */
  desktopWidth: number;
  /** Intrinsic height of the desktop image in pixels */
  desktopHeight: number;
  /**
   * Tailwind classes for the image. Use responsive variants (e.g., `md:absolute`)
   * to apply different styles at different breakpoints.
   */
  className?: string;
  /** Breakpoint at which to switch from mobile to desktop image. Defaults to "md" */
  breakpoint?: Breakpoint;
  /** Whether the image is decorative (sets aria-hidden and empty alt) */
  decorative?: boolean;
  /** Fetch priority hint for LCP optimization. Use "high" for above-the-fold LCP images. */
  fetchPriority?: "high" | "low" | "auto";
}

/**
 * Renders responsive images with mobile and desktop variants using the `<picture>` element.
 *
 * Only the appropriate image for the viewport is loaded, improving performance over
 * CSS-based show/hide approaches. Use responsive Tailwind classes in `className` to
 * apply different styles at different breakpoints.
 *
 * @example
 * ```tsx
 * // Same styling at all breakpoints
 * <ResponsiveImage
 *   mobile={{ src: mobileImg, src2x: mobileImg2x, src3x: mobileImg3x }}
 *   desktop={{ src: desktopImg, src2x: desktopImg2x, src3x: desktopImg3x }}
 *   alt="Description"
 *   mobileWidth={327}
 *   mobileHeight={402}
 *   desktopWidth={716}
 *   desktopHeight={841}
 *   className="h-auto w-full rounded-3xl"
 * />
 *
 * // Different styling per breakpoint
 * <ResponsiveImage
 *   mobile={{ src: mobileImg, src2x: mobileImg2x, src3x: mobileImg3x }}
 *   desktop={{ src: desktopImg, src2x: desktopImg2x, src3x: desktopImg3x }}
 *   alt="Description"
 *   mobileWidth={327}
 *   mobileHeight={402}
 *   desktopWidth={716}
 *   desktopHeight={841}
 *   className="size-16 md:size-24"
 * />
 * ```
 */
export function ResponsiveImage({
  mobile,
  desktop,
  alt,
  mobileWidth,
  mobileHeight,
  desktopWidth,
  desktopHeight,
  className,
  breakpoint = "md",
  decorative = false,
  fetchPriority,
}: ResponsiveImageProps) {
  const mediaQuery = `(min-width: ${BREAKPOINT_VALUES[breakpoint]})`;

  return (
    <picture>
      {/* Desktop source - used when viewport matches media query */}
      <source
        media={mediaQuery}
        srcSet={srcSet(desktop.src, desktop.src2x, desktop.src3x)}
        width={desktopWidth}
        height={desktopHeight}
      />
      {/* Mobile fallback - used on smaller viewports */}
      <img
        src={mobile.src}
        srcSet={srcSet(mobile.src, mobile.src2x, mobile.src3x)}
        alt={decorative ? "" : alt}
        width={mobileWidth}
        height={mobileHeight}
        className={className}
        aria-hidden={decorative || undefined}
        fetchPriority={fetchPriority}
      />
    </picture>
  );
}
